import { Component, OnInit, OnDestroy, Input, ViewChild, Output, EventEmitter, ElementRef } from '@angular/core'
import {
	DemandIntentDocumentAttachmentStatus,
	DemandIntentDocumentAttachment,
	DemandIntentDocumentAttachmentType
} from '@models/demand/demand-intent-document-attachment.model'
import { EMPTY, Observable, map, catchError, of, switchMap, from, finalize, firstValueFrom, interval, Subscription, take } from 'rxjs'
import {
	DemandIntentDocumentPublishStatus,
	DemandIntentDocumentStatus,
	DemandIntentDocument,
	DemandIntentDocumentLoose
} from '@models/demand/demand-intent-document.model'
import { AttachmentResponse } from '@models/attachment/attachment.model'
import { tap, exhaustMap } from 'rxjs/operators'
import { DemandIntentDocumentAttachmentService } from '@services/demand-intent-document-attachment.service'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { FormGroup, FormControl, Validators } from '@angular/forms'
import moment from 'moment-timezone'
import { UploadStatus } from '@models/attachment/upload-attachment.model'
import { UploadS3Service } from '@services/upload-s3.service'
import { ToastService } from '@common/services'
import { BaseResponse } from '@services/api/api-base.service'
import { AlertifyService } from '@services/alertify.service'
import { PncpOrganizationService } from '@services/pncp-organization.service'
import { environment } from '@envs/environment'
import { AccountService } from '@services/account.service'
import { AttachmentService } from '@services/attachment.service'
import { NoticeSession, NoticeSessionStatus } from '@models/planning/notice-session.model'
import { DemandIntentDocumentService, PublishCancelOutcome } from '@services/demand-intent-document.service'
import { DemandIntentDocumentReportType, DemandIntentDocumentReportAttachment } from '@models/demand/demand-intent-document-report.model'
import { DocumentSignatureStatus, DocumentSignatureSourceType, DocumentSignature } from '@models/signature/document-signature.model'
import { DocumentSignatureSigner } from '@models/signature/document-signature-signer.model'
import { FormSignerComponent } from '@components/form-signer/form-signer.component'
import {
	ElectronicProcessDocumentRequest,
	ElectronicProcess,
	ElectronicProcessDocumentSourceType,
	ElectronicProcessDocumentVisibleType
} from '@models/planning/electronic-process.model'
import { ElectronicProcessFormComponent } from '@app/management/planning/electronic-processes/electronic-process-form/electronic-process-form.component'
import { removeErrorTags } from '@utils/str.util'

@Component({
	selector: 'app-attachments',
	templateUrl: './attachments.component.html',
	styleUrl: './attachments.component.scss',
	standalone: false
})
export class AttachmentsComponent implements OnInit, OnDestroy {
	@ViewChild('modalAttachment') modalAttachment: NgbModalRef
	@ViewChild('modalAddAttachment') modalAddAttachment: NgbModalRef
	@ViewChild('modalAttachmentView') modalAttachmentView: NgbModalRef
	@ViewChild('modalElectronicProcess') modalElectronicProcess?: ElementRef
	@ViewChild('formSignerComponent') formSignerComponent?: FormSignerComponent

	@Input() appTitle!: string
	@Input() demand!: DemandIntentDocument
	@Input() onlyComplementary = false
	@Output() published = new EventEmitter()
	@Output() unpublished = new EventEmitter()

	submitted: boolean
	attachmentUrl: any
	attachmentTitle: string
	attachmentTotal: number
	noticeAttachment: boolean
	fallbackNoticeUrl: string
	attachmentLoading: boolean
	submittedAttachment: boolean
	blockSignatureRequest: boolean
	documentEditorLoading: boolean
	attachmentMenuActions: boolean
	allowRemoveSignatureItems: boolean
	submittedAttachmentLoading: boolean
	attachmentNotice$: Observable<AttachmentResponse> = EMPTY
	demandAttachments$: Observable<Array<DemandIntentDocumentAttachment>> = EMPTY
	documentSignatures = new Array<DocumentSignatureSigner>()

	formAttachment = new FormGroup({
		file: new FormControl(null, Validators.compose([Validators.required])),
		description: new FormControl(null, Validators.compose([Validators.required]))
	})

	includeDocumentMode = true
	modalElectronicProcessRef: NgbModalRef
	includeDocumentRequest: ElectronicProcessDocumentRequest | null = null

	private electronicProcessFormComponent?: ElectronicProcessFormComponent
	private waitingToStopPoolingStatusSubscription: Subscription | null = null
	private attachmentPublishPoolingSubscriptions = new Map<string, Subscription>()

	protected readonly DocumentSignatureStatus = DocumentSignatureStatus
	protected readonly DemandIntentDocumentStatus = DemandIntentDocumentStatus
	protected readonly DemandIntentDocumentAttachmentStatus = DemandIntentDocumentAttachmentStatus

	private batchSignatureTargets = new Map<
		string,
		{ demand?: DemandIntentDocument; attachment?: DemandIntentDocumentReportAttachment | DemandIntentDocumentAttachment }
	>()

	constructor(
		private modal: NgbModal,
		private toast: ToastService,
		private alertfy: AlertifyService,
		private accountService: AccountService,
		private uploadS3Service: UploadS3Service,
		private attachmentService: AttachmentService,
		private pncpOrganizationService: PncpOrganizationService,
		public demandIntentDocumentService: DemandIntentDocumentService,
		private demandIntentDocumentAttachmentService: DemandIntentDocumentAttachmentService
	) {}

	ngOnInit() {
		this.fetchAttachment()
		this.fetchDemandAttachments()
	}

	ngOnDestroy() {
		this.stopWaitingToStopPoolingStatus()
		this.stopAllPoolingAttachmentPublish()
	}

	saveAttachment() {
		if (this.formAttachment.invalid) {
			this.formAttachment.markAllAsTouched()
			return
		}

		const file = this.fileAttachmentControl.value

		const entity = new DemandIntentDocumentAttachment({
			date: moment().format('YYYY-MM-DD'),
			type: DemandIntentDocumentAttachmentType.OTHER,
			description: this.descriptionAttachmentControl.value,
			// Tamanho e nome original ficam no metadata no momento do upload — é o que alimenta o
			// badge de tamanho na listagem; anexos gravados antes disso simplesmente não têm badge.
			metadata: { size: file?.size, filename: file?.name }
		})

		entity.demand = new DemandIntentDocument({ id: this.demand.id })

		this.submittedAttachment = true
		this.demandIntentDocumentAttachmentService
			.save(entity)
			.pipe(
				switchMap(response =>
					from(this.uploadS3Service.uploadAttachment(response.id, this.fileAttachmentControl.value)).pipe(
						switchMap(upload => {
							if (upload.status === UploadStatus.UPLOADED) {
								return this.demandIntentDocumentAttachmentService.attached(response.id)
							} else if (upload.status === UploadStatus.ERROR) {
								throw new Error('Upload error')
							}
						})
					)
				),
				finalize(() => (this.submittedAttachment = false))
			)
			.subscribe({
				next: () => {
					this.modal.dismissAll()
					this.fetchDemandAttachments()
					this.toast.success('Documento salvo com sucesso.')
				},
				error: () => this.toast.error('Tívemos um problema ao salvar o documento!')
			})
	}

	cancel(item: DemandIntentDocument) {
		this.alertfy.confirm(
			'Atenção',
			`Deseja cancelar o processo <b>${item.externalCode}</b>, se estiver publicado no PNCP o mesmo será removido!`,
			event => {
				if (event.confirmed) {
					item.deleting = true

					const publishStatus = item.publishStatus
					const published = [DemandIntentDocumentPublishStatus.DONE, DemandIntentDocumentPublishStatus.RESULT_PUBLISH].includes(publishStatus)
					const source = item.isDispute ? this.demandIntentDocumentService.done(item.id) : this.demandIntentDocumentService.cancel(item.id)

					source
						.pipe(
							switchMap(() => {
								if (!published) {
									return of({ status: 'done' } as PublishCancelOutcome)
								}

								// O 202 só confirma o enfileiramento: o desfecho vem do acompanhamento do processo.
								return this.pncpOrganizationService.publishCancel(item.id).pipe(
									map(() => true),
									catchError(() => of(false)),
									switchMap(queued =>
										queued ? this.demandIntentDocumentService.watchPublishCancel(item.id) : of({ status: 'failed' } as PublishCancelOutcome)
									)
								)
							}),
							// Recusado pelo PNCP: o processo não pode ficar cancelado aqui e publicado lá.
							switchMap(outcome =>
								outcome.status === 'failed'
									? this.demandIntentDocumentService.reopen(item.id, publishStatus).pipe(
											map(() => outcome),
											catchError(() => of(outcome))
										)
									: of(outcome)
							)
						)
						.subscribe({
							next: outcome => {
								item.deleting = false

								if (outcome.status === 'failed') {
									// a recusa no enfileiramento já foi exibida pelo interceptor
									if (outcome.demand?.issues) {
										this.toast.error(outcome.demand.issues)
									}
									return
								}

								if (outcome.status === 'timeout') {
									this.toast.warn('O PNCP ainda está processando o cancelamento. Recarregue a tela em instantes para conferir.')
									return
								}

								item.status = DemandIntentDocumentStatus.CANCELED
								item.publishStatus = DemandIntentDocumentPublishStatus.DELETE

								this.unpublished.emit()

								this.toast.success('Processo cancelado com sucesso.')
							},
							error: () => (item.deleting = false)
						})
				}
			}
		)
	}

	removeAttachment(item: DemandIntentDocumentAttachment) {
		this.alertfy.confirm('Atenção', `Deseja remover o documento complementar? Se estiver publicado no PNCP o mesmo será removido!`, event => {
			if (event.confirmed) {
				item.deleting = true

				this.demandIntentDocumentAttachmentService
					.remove(item.id)
					.pipe(
						switchMap(response => {
							if (item.status === DemandIntentDocumentAttachmentStatus.PUBLISHED) {
								return this.pncpOrganizationService.publishRemoveFileOther(this.demand.id, item.id).pipe(
									map(() => response),
									catchError(() =>
										this.demandIntentDocumentAttachmentService
											.recovery(item.id)
											.pipe(switchMap(() => of({ status: 'error' } as BaseResponse)))
									)
								)
							}

							return of({ status: 'success' } as BaseResponse)
						})
					)
					.subscribe({
						next: response => {
							item.deleting = false

							if (response?.status === 'error') {
								return
							}

							this.fetchDemandAttachments()
							this.toast.success('Documento complementar removido com sucesso.')
						},
						error: () => (item.deleting = false)
					})
			}
		})
	}

	publishLoose() {
		this.alertfy.confirm(`Publicar ${this.appTitle}`, 'Deseja realmente publicar este processo?', event => {
			if (event.confirmed) {
				this.submitted = true

				// Cliente-piloto da disputa interna: a publicação NÃO passa pelo pncp-backend — o
				// management marca o processo como publicado (o MESMO bypass do publish do
				// bid-board; o cadastro AVULSO publica por aqui, e era o caminho sem o gate que
				// mandava o piloto ao PNCP em produção). Consulta FRESCA no clique, visível na
				// aba Network; `done = false` preserva o status da demanda avulsa — só o
				// publishStatus muda, como no desfecho do worker do PNCP.
				this.demandIntentDocumentService
					.disputePilotFresh()
					.pipe(take(1))
					.subscribe(disputePilot => {
						console.info(`[Attachments] publishLoose — dispute-pilot: ${disputePilot}`)

						if (disputePilot) {
							this.demandIntentDocumentService
								.publish(this.demand.id, false)
								.pipe(
									switchMap(() => this.demandIntentDocumentService.detail(this.demand.id)),
									finalize(() => (this.submitted = false))
								)
								.subscribe(demand => {
									this.demand = demand
									this.published.emit()
									this.toast.success('Processo publicado.')
								})
							return
						}

						this.pncpOrganizationService.publishLoose(this.demand.id).subscribe({
							next: () => {
								this.demand.status = DemandIntentDocumentStatus.PROCESSING
								this.waitingToStopPoolingStatus()
							},
							error: () => (this.submitted = false)
						})
					})
			}
		})
	}

	publishLooseAttachmentOther(attachment: DemandIntentDocumentAttachment) {
		this.alertfy.confirm('Publicar', 'Deseja realmente publicar o documento complementar?', async event => {
			if (event.confirmed) {
				attachment.issues = null
				attachment.submitted = true

				const response = await firstValueFrom(this.pncpOrganizationService.publishLooseAttachmentOther(this.demand.id, attachment.id))

				if (response.status === 'error') {
					attachment.submitted = false
					this.toast.error(response.message ?? 'Tívemos um problema ao processar a inclusão dos documentos na publicação, tente novamente!')
					return
				}

				this.toast.info('Publicação solicitada, aguardando a confirmação do PNCP.')
				this.waitingToStopPoolingAttachmentPublish(attachment)
			}
		})
	}

	/**
	 * O backend apenas enfileira a publicação do documento complementar (responde 202), quem
	 * publica no PNCP é o worker. O resultado só aparece no próprio anexo: `status` vai para
	 * PUBLISHED em caso de sucesso e `issues` é preenchido em caso de falha — por isso o polling.
	 */
	private waitingToStopPoolingAttachmentPublish(attachment: DemandIntentDocumentAttachment) {
		this.stopPoolingAttachmentPublish(attachment.id)

		const subscription = interval(10000)
			.pipe(
				exhaustMap(total => {
					if (total >= 30) {
						this.stopPoolingAttachmentPublish(attachment.id)
						attachment.submitted = false
						this.toast.warn('Não recebemos a confirmação da publicação, atualize a página em alguns instantes para conferir a situação.')
						return EMPTY
					}

					return this.demandIntentDocumentAttachmentService.detail(attachment.id).pipe(
						catchError(() => {
							this.stopPoolingAttachmentPublish(attachment.id)
							attachment.submitted = false
							return EMPTY
						})
					)
				})
			)
			.subscribe(response => {
				attachment.issues = response.issues
				attachment.status = response.status
				attachment.publishedAt = response.publishedAt

				if (response.status === DemandIntentDocumentAttachmentStatus.PUBLISHED) {
					this.stopPoolingAttachmentPublish(attachment.id)
					attachment.submitted = false
					this.toast.success('Publicado com sucesso.')
					return
				}

				if (response.issues) {
					this.stopPoolingAttachmentPublish(attachment.id)
					attachment.submitted = false
					this.toast.error(removeErrorTags(response.issues) || 'Publicação não realizada no PNCP')
				}
			})

		this.attachmentPublishPoolingSubscriptions.set(attachment.id, subscription)
	}

	private stopPoolingAttachmentPublish(id: string) {
		this.attachmentPublishPoolingSubscriptions.get(id)?.unsubscribe()
		this.attachmentPublishPoolingSubscriptions.delete(id)
	}

	private stopAllPoolingAttachmentPublish() {
		this.attachmentPublishPoolingSubscriptions.forEach(subscription => subscription.unsubscribe())
		this.attachmentPublishPoolingSubscriptions.clear()
	}

	openExternalLinkNoticePncp() {
		const publishId = JSON.parse(this.demand?.publishId)
		const sequential = publishId?.compraUri?.split('/').pop() ?? null
		const exercise = this.demand.exercise
		const document = this.accountService.account.organization.document

		window.open(`${environment.url_pncp}/editais/${document}/${exercise}/${sequential}`, '_blank')
	}

	modalFormAttachment() {
		const modal = this.modal.open(this.modalAddAttachment, { size: 'lg' })

		modal.dismissed.subscribe(() => {
			this.formAttachment.reset()
			this.submittedAttachment = false
			this.fileAttachmentControl.reset()
		})

		modal.closed.subscribe(() => {
			this.formAttachment.reset()
			this.submittedAttachment = false
			this.fileAttachmentControl.reset()
		})
	}

	attachmentDownload(attachment: DemandIntentDocumentAttachment) {
		this.attachmentMenuActions = false
		this.attachmentTitle = 'Documento Complementar'

		this.noticeAttachment = false
		this.modal.open(this.modalAttachmentView, { fullscreen: true })

		this.attachmentLoading = true

		this.attachmentService.downloadHandleBlob(attachment.id).subscribe(url => {
			this.attachmentUrl = url
			this.attachmentLoading = false
		})
	}

	onAttachmentSelected(event: Event) {
		const input = event.target as HTMLInputElement

		if (input.files && input.files.length > 0) {
			const file = input.files[0]

			if (file.size > 40 * 1024 * 1024) {
				this.toast.warn('O tamanho do arquivo não pode maior que 40MB')
				return
			}

			if (file.type !== 'application/pdf') {
				this.fileAttachmentControl.setValue(null)
				this.toast.warn('Formato invalido, informe um arquivo em PDF.')
				return
			}

			this.fileAttachmentControl.setValue(file)
		}
	}

	attachmentNoticeDownload(showModal = true) {
		this.attachmentTitle = 'Edital'
		this.attachmentMenuActions = true
		this.fallbackNoticeUrl = null
		this.blockSignatureRequest = false

		if (showModal) {
			this.noticeAttachment = true
			this.modal.open(this.modalAttachmentView, { fullscreen: true })
		}

		this.attachmentLoading = true

		const { id, created, documentSynced, loose } = this.demand
		const passedFlag = moment(created).isSameOrBefore(moment().subtract(1, 'days'))

		// O edital do PNCP é só a reserva do processo avulso, quando o PDF do S3 não baixa: é nele que o "Alterar
		// Edital" marca o documento como sincronizado (afterAttachmentNoticeUpdateStatusToOpened). No processo com
		// disputa a reserva nunca sairia.
		const pncpFallback = loose && passedFlag && !documentSynced

		this.attachmentService.downloadHandleBlob(id).subscribe(url => {
			if (url === 'error' && pncpFallback) {
				this.attachmentNoticePncpFallback(id)
				return
			}

			this.attachmentUrl = url
			this.attachmentLoading = false
		})
	}

	/** Mostra o link do edital publicado no PNCP e trava a assinatura até o reenvio pelo "Alterar Edital". */
	private attachmentNoticePncpFallback(id: string) {
		this.pncpOrganizationService
			.publishGetFiles(id)
			.pipe(catchError(() => of({ data: [] })))
			.subscribe(response => {
				const document = response?.data?.find(it => it?.tipoDocumentoNome?.toUpperCase() === 'EDITAL')

				this.attachmentUrl = 'error'
				this.attachmentLoading = false

				if (document) {
					this.blockSignatureRequest = true
					this.fallbackNoticeUrl = document.url
				}
			})
	}

	fetchDemandAttachments() {
		if (!this.demand?.id) return
		this.demandAttachments$ = this.demandIntentDocumentAttachmentService.listable(this.demand.id).pipe(
			tap(response => (this.attachmentTotal = response?.length)),
			map(response => response.sort((a, b) => a.ordering - b.ordering)),
			catchError(() => of([]))
		)
	}

	confirmDocumentSignaturesConfirmed() {
		if (this.demand) {
			this.demand.submitted = true
		}
		if (this.documentSignatures?.length > 0) {
			const selectedSourceIds = new Set(this.formSignerComponent?.documentSignatures?.map(it => it.sourceId).filter(Boolean) ?? [])
			for (const [sourceId, target] of this.batchSignatureTargets.entries()) {
				if (!selectedSourceIds.has(sourceId)) {
					this.batchSignatureTargets.delete(sourceId)
					continue
				}

				if (target.attachment) {
					target.attachment.submitted = true
				}
			}
		}
	}

	confirmDocumentSignaturesCanceled() {
		this.allowRemoveSignatureItems = false
		this.batchSignatureTargets.clear()
		this.fetchAttachment()
	}

	confirmDocumentSignaturesStatusUpdated(sourceId: string) {
		if (this.demand && this.demand.id === sourceId) {
			this.demandIntentDocumentService
				.detail(sourceId)
				.pipe(finalize(() => (this.demand.submitted = false)))
				.subscribe(response => {
					this.demand.latestSignature = new DocumentSignature(response?.latestSignature)
				})

			return
		}

		const target = this.batchSignatureTargets.get(sourceId)

		if (target?.attachment) {
			const reportRef = 'reportRef' in target.attachment ? target.attachment.reportRef : undefined

			if (!reportRef?.id) {
				this.demandIntentDocumentAttachmentService
					.detail(sourceId)
					.pipe(
						finalize(() => {
							target.attachment.submitted = false
							this.batchSignatureTargets.delete(sourceId)
							if (this.batchSignatureTargets.size === 0) {
								this.allowRemoveSignatureItems = false
							}
						})
					)
					.subscribe(response => {
						if (response) {
							Object.assign(target.attachment, response)
							target.attachment.latestSignature = new DocumentSignature(response.latestSignature)
						} else {
							this.fetchDemandAttachments()
						}
					})
				return
			}

			this.demandIntentDocumentAttachmentService
				.detail(reportRef.id)
				.pipe(
					finalize(() => {
						target.attachment.submitted = false
						this.batchSignatureTargets.delete(sourceId)
						if (this.batchSignatureTargets.size === 0) {
							this.allowRemoveSignatureItems = false
						}
					})
				)
				.subscribe(response => {
					if (response) {
						Object.assign(target.attachment, response)
						target.attachment.latestSignature = new DocumentSignature(response.latestSignature)
						if ('reportRef' in target.attachment) {
							target.attachment.reportRef = reportRef
						}
					} else {
						this.fetchAttachment()
					}
				})
			return
		}

		this.fetchAttachment()
	}

	async addDocumentElectronicProcesses(document: DemandIntentDocument) {
		if (!document?.id || !this.modalElectronicProcess) {
			return
		}

		this.includeDocumentMode = true
		this.includeDocumentRequest = new ElectronicProcessDocumentRequest({
			ordering: 0,
			title: 'Edital',
			description: null,
			sourceId: document.id,
			sourceType: ElectronicProcessDocumentSourceType.DISPUTE,
			visibleType: ElectronicProcessDocumentVisibleType.PUBLIC,
			date: document?.noticeSession?.openedDate ?? moment().format('YYYY-MM-DD')
		})

		this.modalElectronicProcessRef = this.modal.open(this.modalElectronicProcess, {
			size: 'xl',
			backdrop: 'static',
			keyboard: false
		})

		setTimeout(() => {
			void this.prepareElectronicProcessForm()
		})
	}

	async addAttachmentElectronicProcesses(attachment: DemandIntentDocumentAttachment) {
		if (!attachment?.id || !this.modalElectronicProcess) {
			return
		}

		this.includeDocumentMode = true
		this.includeDocumentRequest = new ElectronicProcessDocumentRequest({
			ordering: 0,
			title: attachment.description,
			description: null,
			sourceId: attachment.id,
			sourceType: ElectronicProcessDocumentSourceType.DISPUTE,
			visibleType: ElectronicProcessDocumentVisibleType.PUBLIC,
			date: attachment.signatureAt ?? attachment.date ?? moment().format('YYYY-MM-DD')
		})

		this.modalElectronicProcessRef = this.modal.open(this.modalElectronicProcess, {
			size: 'xl',
			backdrop: 'static',
			keyboard: false
		})

		setTimeout(() => {
			void this.prepareElectronicProcessForm()
		})
	}

	async onElectronicProcessSaved(processId: string): Promise<void> {
		if (this.modalElectronicProcessRef) {
			this.modalElectronicProcessRef.close()
		}

		await this.refreshDemandElectronicProcess(processId)
	}

	async onElectronicProcessActionCompleted(): Promise<void> {
		if (this.modalElectronicProcessRef) {
			this.modalElectronicProcessRef.close()
		}

		await this.refreshDemandElectronicProcess()
	}

	async attachmentNotice(event: any, sourceId: string, callback?: () => Promise<void>) {
		if (event.target && event.target.files.length > 0) {
			const file = event.target.files[0] as File
			await this.uploadNoticeFile(sourceId, file, callback)
		}
	}

	private async uploadNoticeFile(sourceId: string, file: File, callback?: () => Promise<void>): Promise<boolean> {
		const isPdf = (file.type || '').toLowerCase().includes('pdf') || file.name.toLowerCase().endsWith('.pdf')

		if (!isPdf) {
			this.toast.warn('Informe um documento no formato PDF')
			return false
		}

		if (file.size > 29 * 1024 * 1024) {
			this.toast.warn('O tamanho do arquivo não pode maior que 30MB')
			return false
		}

		const bypassed = this.fallbackNoticeUrl && this.blockSignatureRequest

		if (sourceId === this.demand.id && this.demand.publishStatusDone && !bypassed) {
			this.toast.warn('Antes de incluir um novo documento, favor cancelar o edital atual no PNCP!')
			return false
		}

		this.submittedAttachmentLoading = true
		const response = await this.uploadS3Service.uploadAttachment(sourceId, file)

		if (response.status === UploadStatus.ERROR) {
			this.toast.error('Tívemos um problema ao enviar o arquivo')
			this.submittedAttachmentLoading = false
			return false
		}

		if (callback) {
			await callback()
		} else {
			this.submittedAttachmentLoading = false
		}

		return true
	}

	async delegateDocumentSignatures(demand: DemandIntentDocument, editable = false) {
		this.demand = demand
		this.allowRemoveSignatureItems = !editable
		this.batchSignatureTargets.clear()

		const {
			exercise,
			numProcess,
			id: sourceId,
			noticeSession: { externalCode: numNotice }
		} = this.demand

		const responsibles = [this.demand.responsible]
		const title = `Edital ${numNotice}/${exercise} | Processo ${numProcess}`

		const metadata = {
			title,
			exercise,
			numNotice,
			numProcess,
			docTitle: 'Edital',
			demandId: this.demand.id,
			signatureAt: moment().format('YYYY-MM-DD'),
			documentType: DemandIntentDocumentReportType.NOTICE
		} as any

		if (editable) {
			this.documentSignatures =
				this.demand.latestSignature?.signers?.map(it => {
					metadata.responsibleId = it.responsible.id
					metadata.responsibleName = it.responsible.name

					const model = new DocumentSignatureSigner({
						sourceId,
						owner: it.owner,
						referenceId: sourceId,
						metadata: { ...metadata },
						referenceType: DocumentSignatureSourceType.DID_MANAGEMENT_REPORT
					})

					model.signer = it

					return model
				}) ?? []
		} else {
			this.documentSignatures = responsibles.map(responsible => {
				const {
					individual: { personId: responsibleId, name: responsibleName }
				} = responsible

				const model = new DocumentSignatureSigner({
					sourceId,
					owner: true,
					referenceId: sourceId,
					metadata: { ...metadata },
					referenceType: DocumentSignatureSourceType.DID_MANAGEMENT_REPORT
				})

				model.personControl.setValue({ personId: responsibleId, name: responsibleName })

				return model
			})
		}

		const modalRef = this.modal.open(this.formSignerComponent.modalDocumentSignatures, { keyboard: false, backdrop: 'static', size: 'lg' })
		this.formSignerComponent.registerModalRef(modalRef)

		this.formSignerComponent.editable = editable

		this.documentEditorLoading = false
	}

	async delegateAttachmentSignatures(attachment: DemandIntentDocumentAttachment, editable = false) {
		if (!attachment?.id) {
			return
		}

		this.allowRemoveSignatureItems = !editable
		this.documentEditorLoading = true
		this.batchSignatureTargets.clear()

		try {
			const {
				exercise,
				numProcess,
				noticeSession: { externalCode: numNotice }
			} = this.demand

			const sourceId = attachment.id
			const docTitle = attachment.description
			const signatureAt = attachment.signatureAt ?? attachment.date ?? moment().format('YYYY-MM-DD')
			const title = `${docTitle} | Edital ${numNotice}/${exercise} | Processo ${numProcess}`

			const metadata = {
				title,
				exercise,
				docTitle,
				numNotice,
				numProcess,
				signatureAt,
				documentType: attachment.type,
				demandId: this.demand.id
			} as any

			const {
				individual: { personId: responsibleId, name: responsibleName }
			} = this.demand.responsible

			if (editable && attachment?.latestSignature?.signers?.length > 0) {
				this.documentSignatures = attachment.latestSignature.signers.map(it => {
					const model = new DocumentSignatureSigner({
						sourceId,
						owner: it.owner,
						metadata: {
							...metadata,
							responsibleId: it.responsible.id,
							responsibleName: it.responsible.name
						},
						referenceId: sourceId,
						referenceType: DocumentSignatureSourceType.DID_MANAGEMENT_ATTACHMENT
					})

					model.signer = it
					model.personControl.setValue({ personId: it.responsible.id, name: it.responsible.name })

					return model
				})
			} else {
				const model = new DocumentSignatureSigner({
					owner: true,
					sourceId,
					metadata,
					referenceId: sourceId,
					referenceType: DocumentSignatureSourceType.DID_MANAGEMENT_ATTACHMENT
				})

				model.personControl.setValue({ personId: responsibleId, name: responsibleName })
				this.documentSignatures = [model]
			}

			this.batchSignatureTargets.set(sourceId, { attachment })

			const modalRef = this.modal.open(this.formSignerComponent.modalDocumentSignatures, { keyboard: false, backdrop: 'static', size: 'lg' })
			this.formSignerComponent.registerModalRef(modalRef)
			this.formSignerComponent.editable = editable
		} finally {
			this.documentEditorLoading = false
		}
	}

	protected fetchAttachment() {
		if (!this.demand?.id) return
		this.attachmentNotice$ = this.attachmentService.details(this.demand.id).pipe(catchError(() => of({} as AttachmentResponse)))
	}

	protected async afterAttachmentNoticeUpdateStatusToOpenedReload() {
		await this.afterAttachmentNoticeUpdateStatusToOpened()
		this.attachmentNoticeDownload(false)
	}

	protected async afterAttachmentNoticeUpdateStatusToOpened() {
		if (this.demand.loose) {
			const entity = new DemandIntentDocumentLoose({ noticeSession: new NoticeSession({ status: NoticeSessionStatus.OPENED }) })

			const source = this.demandIntentDocumentService.updateLoose(this.demand.id, entity).pipe(
				tap({
					next: () => {
						this.demandIntentDocumentService.detail(this.demand.id).subscribe({
							next: response => {
								this.demand.noticeSession.status = response.noticeSession.status

								if (response.status !== DemandIntentDocumentStatus.STARTED) {
									this.demandIntentDocumentService
										.reopen(this.demand.id)
										.pipe(finalize(() => (this.submittedAttachmentLoading = false)))
										.subscribe(() => {
											this.demand.status = DemandIntentDocumentStatus.STARTED
										})
								} else {
									this.submittedAttachmentLoading = false
								}
							},
							error: () => (this.submittedAttachmentLoading = false)
						})
					}
				})
			)

			if (this.blockSignatureRequest && this.fallbackNoticeUrl) {
				const entity = new DemandIntentDocument({ documentSynced: true })

				await firstValueFrom(this.demandIntentDocumentService.updateDetails(this.demand.id, entity))

				this.fallbackNoticeUrl = null
				this.demand.documentSynced = true
				this.blockSignatureRequest = false
				this.submittedAttachmentLoading = false
			} else {
				await firstValueFrom(source)
			}
		}

		this.fetchAttachment()
	}

	private async refreshDemandElectronicProcess(fallbackProcessId?: string): Promise<void> {
		if (!this.demand?.id) {
			return
		}

		const updatedDemand = await firstValueFrom(this.demandIntentDocumentService.detail(this.demand.id))
		this.demand.electronicProcess = updatedDemand.electronicProcess ?? (fallbackProcessId ? new ElectronicProcess({ id: fallbackProcessId }) : null)
	}

	private async prepareElectronicProcessForm(): Promise<void> {
		if (!this.electronicProcessFormComponent) {
			return
		}

		if (this.includeDocumentMode) {
			await this.electronicProcessFormComponent.prepareIncludeDocumentMode()
			return
		}

		if (!this.demand?.id) {
			return
		}

		const processId = this.demand.electronicProcess?.id

		if (processId) {
			await this.electronicProcessFormComponent.loadProcess(processId, this.demand.id)
			return
		}

		await this.electronicProcessFormComponent.prepareCreate(this.demand.id)
	}

	private waitingToStopPoolingStatus() {
		this.stopWaitingToStopPoolingStatus()

		this.waitingToStopPoolingStatusSubscription = interval(10000)
			.pipe(
				exhaustMap(total => {
					if (total >= 20) {
						this.stopWaitingToStopPoolingStatus()
						this.demand.publishStatus = DemandIntentDocumentPublishStatus.ERROR_PUBLISH
						this.submitted = false
						this.toast.error('Publicação não realizada no PNCP')
						return EMPTY
					}

					return this.demandIntentDocumentService.detail(this.demand.id).pipe(
						catchError(() => {
							this.stopWaitingToStopPoolingStatus()
							this.submitted = false
							return EMPTY
						})
					)
				})
			)
			.subscribe(demand => {
				this.demand = demand

				if (this.demand.publishStatus === DemandIntentDocumentPublishStatus.DONE) {
					this.stopWaitingToStopPoolingStatus()
					this.published.emit()
					this.submitted = false
					this.toast.success('Publicado com sucesso no PNCP')
				}

				if (this.demand.publishStatus === DemandIntentDocumentPublishStatus.ERROR_PUBLISH) {
					this.stopWaitingToStopPoolingStatus()
					this.submitted = false
					this.toast.error('Publicação não realizada no PNCP')
				}
			})
	}

	private stopWaitingToStopPoolingStatus() {
		if (this.waitingToStopPoolingStatusSubscription) {
			this.waitingToStopPoolingStatusSubscription.unsubscribe()
			this.waitingToStopPoolingStatusSubscription = null
		}
	}

	get descriptionAttachmentControl(): FormControl {
		return this.formAttachment.get('description') as FormControl
	}

	get descriptionAttachmentControlInvalid(): boolean {
		return this.descriptionAttachmentControl.invalid && this.descriptionAttachmentControl.touched
	}

	get fileAttachmentControl(): FormControl {
		return this.formAttachment.get('file') as FormControl
	}

	get fileAttachmentControlInvalid(): boolean {
		return this.fileAttachmentControl.invalid && this.fileAttachmentControl.touched
	}

	get sourceIds() {
		if (this.documentSignatures?.length > 0) {
			return [...new Set(this.documentSignatures.map(it => it.sourceId).filter(Boolean))]
		}
		if (this.demand?.id) return [this.demand.id]
		return []
	}
}
